/**
 * The Invest menu: Copy Trading, Smart Portfolios and Meme coins under one top-bar entry, so the bar
 * stays on one row however many of them are switched on. Each is a column with its picture card and
 * pages, and shows only while its feature is on for the client (stores/features); with none on, the
 * menu isn't there at all.
 */
import type { FeatureAccess } from "@platform/shared";
import { useFeatures } from "../stores/features";
import { MegaMenu, type MegaColumn, type NavItem } from "./MegaMenu";

export interface InvestItem extends NavItem {
  feature: keyof FeatureAccess;
  /** The item's column in the Invest menu, also the picture cards in the phone menu. */
  column: MegaColumn & { promo: NonNullable<MegaColumn["promo"]> };
}

export const INVEST_ITEMS: InvestItem[] = [
  {
    feature: "copy_trading",
    to: "/copy",
    label: "Copy Trading",
    icon: "copy",
    prefixes: ["/copy"],
    column: {
      title: "Copy Trading",
      promo: { to: "/copy", image: "/menu/copy-trading.webp", title: "Copy the pros", cta: "Explore traders", tone: "accent" },
      links: [
        { to: "/copy", label: "Browse traders", blurb: "Copy experienced traders automatically", glyph: "copy", prefixes: ["/copy/trader", "/copy/copies"] },
        { to: "/copy/lead-trader", label: "Become a lead trader", blurb: "Share your trades and earn from followers", glyph: "star", prefixes: ["/copy/lead-trader"] },
      ],
    },
  },
  {
    feature: "smart_portfolios",
    to: "/portfolios",
    label: "Smart Portfolios",
    icon: "portfolio",
    prefixes: ["/portfolios", "/portfolio/smart"],
    column: {
      title: "Smart Portfolios",
      promo: { to: "/portfolios", image: "/menu/smart-portfolios.webp", title: "Ready-made baskets", cta: "See portfolios", tone: "up" },
      links: [
        { to: "/portfolios", label: "Browse portfolios", blurb: "Ready-made baskets, rebalanced for you", glyph: "pie", prefixes: ["/portfolios"] },
        { to: "/portfolio", label: "My smart portfolios", blurb: "Track what you've invested", glyph: "chart", prefixes: ["/portfolio/smart"] },
      ],
    },
  },
  {
    feature: "meme",
    to: "/meme",
    label: "Meme coins",
    icon: "zap",
    prefixes: ["/meme"],
    column: {
      title: "Meme coins",
      promo: { to: "/meme", image: "/menu/meme-coins.webp", title: "Meme coin markets", cta: "Trade memes", tone: "warn" },
      links: [{ to: "/meme", label: "Meme coins", blurb: "Trade meme coin prices", glyph: "zap", prefixes: ["/meme"] }],
    },
  },
];

/** The Invest items this client can see, in menu order. */
export function useInvestItems(): InvestItem[] {
  const access = useFeatures();
  return INVEST_ITEMS.filter((i) => access[i.feature]);
}

export function InvestMenu() {
  return <MegaMenu label="Invest" columns={useInvestItems().map((i) => i.column)} />;
}
